import { useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { deleteSection, sectionName } from '../db/sections';
import { countInSection } from '../db/board';
import type { Section } from '../db/schema';

/**
 * Unlike deleting a room, this destroys nothing. A section is an arrangement
 * of things you already own, so dropping the arrangement puts the things back
 * where it was — and the dialog says which "back" that is, because "delete"
 * on a container that holds photos is a frightening word otherwise.
 */
export function DeleteSectionDialog({
  section,
  parentName,
  onClose,
  onDeleted,
}: {
  section: Section;
  /** Where its contents will land: the room, or the section above it. */
  parentName: string;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const count = useLiveQuery(() => countInSection(section.id), [section.id]);

  async function confirm() {
    setBusy(true);
    setError(undefined);
    try {
      await deleteSection(section.id);
      onDeleted();
    } catch {
      setError("That section couldn't be deleted. Nothing was changed.");
      setBusy(false);
    }
  }

  return (
    <Dialog title={`Delete ${sectionName(section)}?`} onClose={onClose}>
      <p>
        {count === undefined || count === 0
          ? `Nothing is inside it, so nothing moves.`
          : `Its ${count === 1 ? 'item moves' : `${count} items move`} back into ${parentName}. No photos are deleted.`}
      </p>

      {error !== undefined && (
        <p className="dialog__error" role="alert">
          {error}
        </p>
      )}

      <DialogActions>
        <Button variant="quiet" onClick={onClose} disabled={busy}>
          Keep It
        </Button>
        <Button variant="destructive" onClick={() => void confirm()} disabled={busy}>
          Delete Section
        </Button>
      </DialogActions>
    </Dialog>
  );
}
